import { useState } from 'react'
import { LazyAvatar } from '@/components/common/LazyAvatar'
import { getCTALabel } from '@/components/mediaBuying/adCreatives/screens/adContent/CTADropdown'
import iconPlaceholder from '@/assets/images/media-buying/icon-placeholder.png'
import centerMockPhone from '@/assets/images/media-buying/center-mock-phone.png'
import sponsorIcon from '@/assets/images/media-buying/sponsor-icon.png'
import bottomBarMockPhone from '@/assets/images/media-buying/bottom-bar-mock-phone.png'
import { Media } from '@/components/mediaBuying/adCreatives/screens/mediaCrop/Media.jsx'
import { truncateHeadline, truncateDescription, isTextSingleLine } from '../ad-preview'
import { constructProxyUrl } from '@/utils/proxy.js'
import { getFormatMediaProps } from './getFormatMediaProps'

const CarouselPreviewHorizontal = ({
    assets: passedAssets,
    primaryText,
    description,
    cta,
    headline,
    pageTitle,
    pageIcon,
    showDefaultPrimaryText,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [assets] = useState(passedAssets?.map((asset) => ({ ...asset, url: constructProxyUrl(asset.url) })) || [])
    const currentAsset = assets[currentIndex] || {}
    const assetVariant = currentAsset.variations?.find((x) => x.format === 'horizontal')
    const cropData = assetVariant?.crop_data

    if (!assetVariant) {
        return (
            <div className="tw:!max-w-[305px] tw:!w-[305px] tw:h-[160px] tw:bg-gray-200 tw:rounded-[23px] tw:flex tw:items-center tw:justify-center tw:text-gray-500">
                No horizontal preview
            </div>
        )
    }

    const handlePrevious = () => setCurrentIndex((prev) => (prev > 0 ? prev - 1 : assets.length - 1))
    const handleNext = () => setCurrentIndex((prev) => (prev < assets.length - 1 ? prev + 1 : 0))

    return (
        <div className="tw:!max-w-[305px] tw:!w-[305px] tw:bg-white tw:border tw:border-gray-300 tw:rounded-[23px] tw:overflow-hidden">
            {/* Header */}
            <div className="tw:flex tw:flex-column tw:p-3 tw:border-b tw:border-gray-200">
                <LazyAvatar
                    src={pageIcon}
                    placeholder={iconPlaceholder}
                    alt={'Page Image'}
                    className="tw:w-6 tw:h-6 tw:rounded-full"
                />
                <div className="tw:flex-1">
                    <div className="tw:text-sm tw:font-medium tw:text-gray-900 tw:pl-1">{pageTitle}</div>
                    <div className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:pl-2 tw:text-gray-500">
                        <span>Sponsored</span>
                        <img src={sponsorIcon} alt="sponsor Icon" className="tw:w-3 tw:h-3" />
                    </div>
                </div>
                <div className="tw:text-gray-400 tw:text-lg">⋯</div>
            </div>

            <div className="tw:flex">
                <div className="tw:w-100 tw:h-[160px] tw:relative tw:overflow-hidden tw:bg-gray-100">
                    <Media
                        type={currentAsset?.type}
                        url={currentAsset?.url || ''}
                        canvasWidth={assetVariant?.cropped ? cropData?.width : 241}
                        canvasHeight={assetVariant?.cropped ? cropData?.height : 128}
                        cropData={assetVariant?.cropped ? cropData : null}
                        isProxied={true}
                        autoStart={false}
                        startTime={currentAsset?.trim_data?.start}
                        endTime={currentAsset?.trim_data?.end}
                        thumbnails={currentAsset.thumbnails}
                    />

                    {/* Navigation Arrows - Positioned over the image */}
                    {assets.length > 1 && (
                        <>
                            <button
                                onClick={handlePrevious}
                                className="tw:absolute tw:left-2 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-11 tw:h-11 tw:bg-white/80 tw:backdrop-blur-sm tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:!border-0 tw:transition-all hover:tw:bg-white/90"
                            >
                                <span className="tw:text-gray-700 tw:font-bold tw:text-xl">‹</span>
                            </button>
                            <button
                                onClick={handleNext}
                                className="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:z-10 tw:w-11 tw:h-11 tw:bg-white/80 tw:backdrop-blur-sm tw:!rounded-full tw:flex tw:items-center tw:justify-center tw:!border-0 tw:transition-all hover:tw:bg-white/90"
                            >
                                <span className="tw:text-gray-700 tw:font-bold tw:text-xl">›</span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="tw:flex-1 tw:flex tw:flex-col tw:justify-between">
                <div className="tw:font-bold tw:text-base tw:text-gray-900 tw:leading-tight tw:pt-3 tw:pl-4  overflow-ellipse tw:whitespace-nowrap tw:w-[285px]">
                    {headline || 'Headline'}
                </div>
                {(primaryText || showDefaultPrimaryText) && (
                    <div className="tw:pt-1 tw:pl-4 tw:text-sm tw:text-gray-900 tw:leading-snug  overflow-ellipse tw:whitespace-nowrap tw:w-[285px]">
                        {showDefaultPrimaryText
                            ? "Dreaming of a carefree beach escape? We'll find the perfect tour to bring you sun sea and total relaxation."
                            : primaryText}
                    </div>
                )}

                {/* Description - Only show if headline is single line */}
                {description && (
                    <div className="tw:pt-1 tw:pl-4 tw:text-xs tw:text-gray-600 tw:leading-snug">{description}</div>
                )}

                {/* CTA Button */}
                <div className="tw:bg-transparent tw:bg-opacity-95 tw:rounded-lg tw:pt-3 tw:px-2 tw:text-black">
                    <button className="tw:w-full tw:bg-blue-500/85 tw:text-white tw:py-2 tw:px-4 tw:!rounded-md tw:text-sm tw:font-semibold">
                        {getCTALabel(cta)}
                    </button>
                </div>
            </div>

            {/* Engagement bar - PRESERVED */}
            <div className="tw:flex-shrink-0">
                <hr
                    className="border-gray-700 my-1 mt-2"
                    style={{
                        height: '2px',
                        color: 'gray',
                        marginTop: 0,
                        marginBottom: 0,
                    }}
                />
                <img src={bottomBarMockPhone} alt="comment" className="p-2 pb-3" />
            </div>
        </div>
    )
}

export const AdPreviewHorizontal = ({
    assetVariant,
    primaryText = '',
    headline = '',
    description = '',
    cta = 'Learn More',
    pageTitle = 'Page Name',
    pageIcon = iconPlaceholder,
    showDefaultPrimaryText = false,
    assetData = {},
    carouselAssets = [],
    adCreativeFormat,
    cropStateFromMediaCrop = [],
}) => {
    // Use first variation if arrays are passed and apply truncation
    const rawPrimaryText = Array.isArray(primaryText) ? primaryText[0] : primaryText
    const rawHeadline = Array.isArray(headline) ? headline[0] : headline
    const rawDescription = Array.isArray(description) ? description[0] : description

    // Apply truncation rules
    const displayPrimaryText = rawPrimaryText // Keep primary text unchanged
    const displayHeadline = truncateHeadline(rawHeadline) // Truncate headline to 40 chars

    // Show description only if headline takes 1 line (wider container for horizontal)
    const headlineIsSingleLine = isTextSingleLine(displayHeadline, 350, 14)
    const displayDescription = headlineIsSingleLine ? truncateDescription(rawDescription) : ''

    const { url, cropData, canvasWidth, canvasHeight } = getFormatMediaProps({
        format: 'horizontal',
        cropStateFromMediaCrop,
        assetData,
        assetVariant,
        defaultWidth: 241,
        defaultHeight: 126,
    })

    const hasMedia = !!(assetVariant || assetData?.url || cropStateFromMediaCrop?.find((c) => c.id === 'horizontal')?.originalUrl)

    return (
        <div className="tw:flex  overflow-x-hidden">
            {/* Check if we have carousel assets */}
            {adCreativeFormat === 'carousel' ? (
                <CarouselPreviewHorizontal
                    assetVariant={assetVariant}
                    assets={carouselAssets}
                    primaryText={displayPrimaryText}
                    headline={displayHeadline}
                    description={displayDescription}
                    cta={cta}
                    pageTitle={pageTitle}
                    pageIcon={pageIcon}
                    showDefaultPrimaryText={showDefaultPrimaryText}
                />
            ) : !hasMedia ? (
                <div className="tw:!max-w-[305px] tw:!w-[305px] tw:h-[160px] tw:bg-gray-200 tw:rounded-[23px] tw:flex tw:items-center tw:justify-center tw:text-gray-500">
                    No horizontal preview available
                </div>
            ) : (
                <div className="tw:!max-w-[305px] tw:!w-[305px] tw:bg-white tw:border tw:border-gray-300 tw:rounded-[23px] tw:overflow-hidden">
                    {/* Header */}
                    <div className="tw:flex tw:flex-column tw:p-3 tw:border-b tw:border-gray-200">
                        <LazyAvatar
                            src={pageIcon}
                            placeholder={iconPlaceholder}
                            alt={'Page Image'}
                            className="tw:w-6 tw:h-6 tw:rounded-full"
                        />
                        <div className="tw:flex-1">
                            <div className="tw:text-sm tw:font-medium tw:text-gray-900 tw:pl-1">{pageTitle}</div>
                            <div className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:pl-2 tw:text-gray-500">
                                <span>Sponsored</span>
                                <img src={sponsorIcon} alt="sponsor Icon" className="tw:w-3 tw:h-3" />
                            </div>
                        </div>
                        <div className="tw:text-gray-400 tw:text-lg">⋯</div>
                    </div>

                    <div className="tw:flex">
                        <div className="tw:w-100 tw:h-[160px] tw:relative tw:overflow-hidden tw:bg-gray-100">
                            {url ? (
                                <Media
                                    type={assetData?.type}
                                    url={url}
                                    canvasWidth={canvasWidth}
                                    canvasHeight={canvasHeight}
                                    cropData={cropData}
                                    isProxied={true}
                                    autoStart={false}
                                    startTime={assetVariant?.adCreativeAsset?.trim_data?.start ?? assetData?.trim_data?.start}
                                    endTime={assetVariant?.adCreativeAsset?.trim_data?.end ?? assetData?.trim_data?.end}
                                    thumbnails={assetData?.thumbnails}
                                />
                            ) : (
                                <img
                                    src={centerMockPhone}
                                    alt="Preview"
                                    className="tw:w-full tw:h-full tw:object-cover"
                                />
                            )}
                        </div>
                    </div>

                    <div className="tw:flex-1 tw:flex tw:flex-col tw:justify-between">
                        <div className="tw:font-bold tw:text-base tw:text-gray-900 tw:leading-tight tw:pt-3 tw:pl-4 overflow-ellipse tw:whitespace-nowrap tw:w-[180px]">
                            {displayHeadline || 'Headline'}
                        </div>
                        {(displayPrimaryText || showDefaultPrimaryText) && (
                            <div className="tw:pt-1 tw:pl-4 tw:text-sm tw:text-gray-900 tw:leading-snug">
                                {showDefaultPrimaryText
                                    ? "Dreaming of a carefree beach escape? We'll find the perfect tour to bring you sun sea and total relaxation."
                                    : displayPrimaryText}
                            </div>
                        )}

                        {/* Description - Only show if headline is single line */}
                        {displayDescription && (
                            <div className="tw:pt-1 tw:pl-4 tw:text-xs tw:text-gray-600 tw:leading-snug">
                                {displayDescription}
                            </div>
                        )}

                        {/* CTA Button */}
                        <div className="tw:bg-transparent tw:bg-opacity-95 tw:rounded-lg tw:pt-3 tw:px-2 tw:text-black">
                            <button className="tw:w-full tw:bg-blue-500/85 tw:text-white tw:py-2 tw:px-4 tw:!rounded-md tw:text-sm tw:font-semibold">
                                {getCTALabel(cta)}
                            </button>
                        </div>
                    </div>

                    {/* Engagement bar */}
                    <div className="tw:flex-shrink-0">
                        <hr
                            className="border-gray-700 my-1 mt-2"
                            style={{
                                height: '2px',
                                color: 'gray',
                                marginTop: 0,
                                marginBottom: 0,
                            }}
                        />
                        <img src={bottomBarMockPhone} alt="comment" className="p-2 pb-3" />
                    </div>
                </div>
            )}
        </div>
    )
}
